import { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Typography, Card, Button, useTheme } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppConfig } from 'AppConfig';
import { fetchData } from 'features/user/userActions';
import { hasPermission, hasSettingsAccess } from 'utils/common';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import * as UserTypes from 'features/user/userTypes';
import routePaths from 'routes/routePaths';

export default function Settings() {
  const theme = useTheme();
  const permissions = useSelector((state) => state.user.permissions);
  const externalUser = useSelector((state) => state.user.external);
  const currentApp = useSelector((state) => state.user.currentApp);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const apps = useSelector((state) => state.user.apps);
  const isUpdating = useSelector((state) => state.user.isUpdating);
  const defaultUserState = useSelector((state) => state.user.defaultUserState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pendingSelectedTenantNavigation, setPendingSelectedTenantNavigation] = useState(null);
  const REPORT_DEFAULT_SETTING = routePaths.reportDefaultSetting;
  const LOOKUP_URL = `${routePaths.lookup}/${AppConfig.LOOKUP_TABLES_SUB_URL.LOOKUP}`;
  const HOME_URL = routePaths.frontOffice;

  const permissionsHydrated = !defaultUserState && !isUpdating;
  const canAccessSettings = hasSettingsAccess({ permissions, externalUser, apps });

  useEffect(() => {
    if (permissionsHydrated && !canAccessSettings) {
      navigate(HOME_URL, { replace: true });
    }
  }, [permissionsHydrated, canAccessSettings, navigate, HOME_URL]);

  const handleClickGlobalSettings = useCallback((url) => {
    navigate(url);
  }, [navigate]);

  const handleClick = useCallback(async (url, targetApp) => {
    if (targetApp !== currentApp) {
      await dispatch(UserTypes.resetAppsTenant());
    }

    navigate(url);
  }, [currentApp, dispatch, navigate]); // Original app-switch flow, kept for Master Data and Lookup app-switch only. Using the new handleClickSelectedTenantSettings() for Mastere Data and Lookup switch caused unknown behavior. Needs further investigation.

  const handleClickSelectedTenantSettings = useCallback(async (url, targetApp = AppConfig.APP_CODE_FO) => {
    if (!currentTenantCd) {
      setPendingSelectedTenantNavigation({ url, targetApp });
      return;
    }

    if (targetApp !== currentApp) {
      await dispatch(fetchData(currentTenantCd, targetApp, { explicitTenant: false }));
    }

    navigate(url);
  }, [currentApp, currentTenantCd, dispatch, navigate]);

  useEffect(() => {
    if (!pendingSelectedTenantNavigation || !currentTenantCd) {
      return;
    }

    setPendingSelectedTenantNavigation(null);
    void handleClickSelectedTenantSettings(
      pendingSelectedTenantNavigation.url,
      pendingSelectedTenantNavigation.targetApp
    );
  }, [currentTenantCd, handleClickSelectedTenantSettings, pendingSelectedTenantNavigation]);

  const settings = useMemo(() => [
    {
      title: 'Global Configuration',
      description: 'Configure global settings and default preferences.',
      onClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.FO_REPORT_MANAGEMENT);
        handleClickGlobalSettings(REPORT_DEFAULT_SETTING);
      },
      isVisible: hasPermission(AppConfig.PERMISSION.FO_REPORT_MANAGEMENT, permissions)
    },
    {
      title: 'Lookup',
      description: 'Manage lookup values and reference data.',
      onClick: () => {
        dispatch({
          type: UserTypes.APP_SWITCHED,
          payload: AppConfig.HEADER_MENU_TYPE.NONE
        });
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.MD_LKP);
        handleClickGlobalSettings(LOOKUP_URL); // Direct navigation to Lookup, let the LookupTableNav perform the master-data switch. 
      },
      isVisible: hasPermission(AppConfig.PERMISSION.MD_LKP, permissions)
    },
    {
      title: 'Manage Reports',
      description: 'Review and Update existing reports.',
      onClick: () => {
        handleClickSelectedTenantSettings(`${HOME_URL}${AppConfig.FRONT_OFFICE_SUB_URL.MANAGE_REPORTS}`);
      },
      isVisible: externalUser !== 'true' && hasPermission(AppConfig.PERMISSION.FO_REPORT_MANAGEMENT, permissions)
    },
    {
      title: 'Add Reports',
      description: 'Create and Add new reports to the system.',
      onClick: () => {
        handleClickSelectedTenantSettings(`${HOME_URL}${AppConfig.FRONT_OFFICE_SUB_URL.ADD_REPORTS}`);
      },
      isVisible: externalUser !== 'true' && hasPermission(AppConfig.PERMISSION.FO_REPORT_MANAGEMENT, permissions)
    },
    {
      title: 'Master Data',
      description: 'Maintain core and shared master data.',
      onClick: () => {
        dispatch({
          type: UserTypes.APP_SWITCHED,
          payload: AppConfig.HEADER_MENU_TYPE.STANDARD
        });
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.APP_CODE_MASTER_DATA);
        handleClick(`${routePaths.master}/${AppConfig.MASTER_DATA_SUB_URL.MASTER_TABLE}`, AppConfig.APP_CODE_MASTER_DATA);
      },
      isVisible: apps?.includes(AppConfig.APP_CODE_MASTER_DATA)
    },
    {
      title: 'Tenant Admin',
      description: 'Configure tenant‑level settings and controls.',
      onClick: () => {
        dispatch({
          type: UserTypes.APP_SWITCHED,
          payload: AppConfig.HEADER_MENU_TYPE.NONE
        });
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.FO_LOCAL_TENANT_MANAGEMENT);
        handleClickGlobalSettings(routePaths.tenantAdmin);
      },
      isVisible: hasPermission(AppConfig.PERMISSION.FO_LOCAL_TENANT_MANAGEMENT, permissions)
    },
    {
      title: 'Tenant Management',
      description: 'Manage tenant configuration, status, and details.',
      onClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.FO_COMPANY_MANAGEMENT);
        handleClickGlobalSettings(`${routePaths.tenantManagement}/${AppConfig.TENANT_MANAGEMENT_SUB_URL.TENANT}`);
      },
      isVisible: hasPermission(AppConfig.PERMISSION.FO_COMPANY_MANAGEMENT, permissions)
    },
    {
      title: 'White Label',
      description: 'Customize branding and tenant appearance.',
      onClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.LOGO_MANAGEMENT);
        handleClickGlobalSettings(`${routePaths.whiteLabeling}/${AppConfig.WHITE_LABEL_SUB_URL.WHITE_LABEL}`);
      },
      isVisible: hasPermission(AppConfig.PERMISSION.LOGO_MANAGEMENT, permissions)
    },
    {
      title: 'User Admin',
      description: 'Oversee users, roles, and access permissions.',
      onClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.USER_PERMISSION_ADMIN);
        handleClickGlobalSettings(routePaths.adminPage);
      },
      isVisible: hasPermission(AppConfig.PERMISSION.USER_PERMISSION_ADMIN, permissions)
    }
  ], [apps, externalUser, handleClick, handleClickGlobalSettings, handleClickSelectedTenantSettings, permissions]);

  if (permissionsHydrated && !canAccessSettings) {
    return null;
  }
  return (
    <Box sx={{ bgcolor: '#FFFFFF', py: '32px', mx: '-60px', px: { xs: 2, sm: 6, md: 6 } }}>
      <Typography
        sx={{
          color: theme.palette.common.black,
          fontFamily: 'Rubik',
          fontSize: '20px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '1.875rem',
          letterSpacing: '0.0125rem'               
        }}
      >
        Settings
      </Typography>
      <Grid container spacing={3} pt={3}>
        {settings.map((setting) => {
          return setting.isVisible ? (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={setting.title}>
              <Card 
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #71767A',
                  boxShadow: 'none',
                  background: theme.palette.common.white,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  padding: '24px'
                }}
              >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: { xs: '4px', sm: '16px' },
                  minWidth: 0,
                  flexWrap: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    minHeight: 'auto',
                    maxHeight: '48px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flex: '1 1 0',
                    minWidth: 0,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    mt: '0px'
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.common.black,
                      fontFamily: 'Rubik',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '24px',
                      letterSpacing: '0.16px'
                    }}
                  >
                    {setting.title}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  marginTop: '24px',
                  height: '45px'
                }}
              >
                <Typography
                  sx={{
                    color: '#121A22',
                    fontFamily: 'Noto Sans',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '21px',
                    leadingTrim: 'both',
                    textEdge: 'cap'
                  }}>
                  {setting.description}
                </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexShrink: 0,
                    marginTop: '24px'
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      height: '28px',
                      padding: '8px 10px',
                      borderRadius: '24px',
                      border: '1px solid #2F54A5',
                      textTransform: 'none',
                      color: '#2F54A5',
                      '&:hover': {
                        borderColor: '#001A47',
                        color: '#001A47',
                        backgroundColor: '#D8E6FF'
                      }
                    }}  
                    onClick={setting.onClick}
                  >
                    <Typography 
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Noto Sans',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: '16.8px',
                        leadingTrim: 'both',
                        textAlign: 'center',
                        textEdge: 'cap',
                      }}>
                      Select
                    </Typography>
                  </Button>
                </Box>
            </Card>
          </Grid>) : null;
      }  )}
      </Grid>
    </Box>
    );
}
